// src/pages/components/MateriaisRiscoDashboard.js
import { useEffect, useState, useMemo } from "react";
import {
  Card,
  CircularProgress,
  Checkbox,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import MenuSearchMateriais from "pages/components/MenuSearchMateriais";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import InfoIcon from "@mui/icons-material/Info";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export default function MateriaisRiscoDashboard({ podeVerRuturas }) {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const [materiaisRisco, setMateriaisRisco] = useState([]);
  const [loading, setLoading] = useState(false);

  const [mostrarInativos, setMostrarInativos] = useState(false);

  // ---------------------------------------------------------------------------
  // Centros (mesma lógica do AnaliseStocks)
  // ---------------------------------------------------------------------------
  const [centrosPossiveis, setCentrosPossiveis] = useState([]); // [ [refcentro, nome] ]
  const [centrosSel, setCentrosSel] = useState([]); // [refcentro]
  const [centrosUserRefs, setCentrosUserRefs] = useState([]); // [refcentro] associados ao utilizador

  // ---------------------------------------------------------------------------
  // diálogos de ATIVAR MATERIAL
  // ---------------------------------------------------------------------------
  const abrirDialogAtivar = (mat) => {
    setMaterialRiscoSel(mat);
    setOpenDialogAtivar(true);
  };

  const handleCancelarAtivar = () => {
    setOpenDialogAtivar(false);
    setMaterialRiscoSel(null);
  };

  const handleConfirmarAtivar = async () => {
    if (!materialRiscoSel) return;

    try {
      const body = {
        refmaterial: materialRiscoSel.refmaterial,
        inativo: 0,
      };

      const res = await api.post("Materiais/UpdMaterialInativo", body);
      const dados = res.data;
      const msg = (dados && dados.message) || "Material ativado com sucesso.";

      showAlert(msg, "success");

      setMateriaisRisco((prev) => {
        // se estiver desligado, o material já não está na lista (porque estava inativo)
        // mas se por acaso estiver, atualiza-o:
        const atualizada = prev.map((m) =>
          m.refmaterial === materialRiscoSel.refmaterial ? { ...m, inativo: 0 } : m
        );

        return atualizada;
      });
    } catch {
      showAlert("Erro ao ativar material.", "error");
    } finally {
      handleCancelarAtivar();
    }
  };

  // diálogos de INATIVAR / DIAS RUTURA
  const [openDialogInativar, setOpenDialogInativar] = useState(false);
  const [materialRiscoSel, setMaterialRiscoSel] = useState(null);
  const [openDialogAtivar, setOpenDialogAtivar] = useState(false);

  const [imgsByRef, setImgsByRef] = useState({});

  const [dialogDiasRutura, setDialogDiasRutura] = useState({
    open: false,
    refmaterial: "",
    nome: "",
    valorAntigo: 30,
    valorNovo: 30,
  });

  const [detalhesOpen, setDetalhesOpen] = useState(false);
  const [materialDetalhe, setMaterialDetalhe] = useState(null);

  const [filtros, setFiltros] = useState({
    fabricante: "",
    refmaterial: "",
    nomematerial: "",
  });

  // tooltips iguais aos de AnaliseStocks
  const tooltipMediaAno = useMemo(
    () => (
      <div style={{ fontSize: 11, maxWidth: 360 }}>
        <strong>Dias Rutura (média ano)</strong>
        <br />
        Calculado com:
        <br />
        • Vendas do material nos últimos 365 dias (até à data atual);
        <br />
        • Soma das quantidades vendidas nesse período;
        <br />
        • Consumo diário = total vendido / nº de dias do período;
        <br />• Dias de rutura = stock total atual / consumo diário.
      </div>
    ),
    []
  );

  const tooltipMax2Meses = useMemo(
    () => (
      <div style={{ fontSize: 11, maxWidth: 360 }}>
        <strong>Dias Rutura (prev. mês seg.)</strong>
        <br />• Considera a sazonalidade do <u>mês seguinte</u> ao atual;
        <br />
        • Procura o mesmo mês no último ano;
        <br />
        • Consumo diário = vendas desse mês / 30;
        <br />• Dias de rutura = stock total atual / consumo diário sazonal.
      </div>
    ),
    []
  );

  const carregarImagensMateriais = async (refs) => {
    const lista = Array.from(
      new Set((refs || []).map((r) => String(r || "").trim()).filter(Boolean))
    );

    const faltam = lista.filter((r) => !imgsByRef[r]);
    if (faltam.length === 0) return;

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMateriaisImagens", { refs: faltam })
    );

    if (err) return;

    const mapa = {};
    (Array.isArray(dados) ? dados : []).forEach((x) => {
      const ref = String(x?.refmaterial || "").trim();
      const url = String(x?.url || "").trim();
      if (ref && url) mapa[ref] = url;
    });

    setImgsByRef((prev) => ({ ...(prev || {}), ...mapa }));
  };

  const carregarCentrosPossiveis = async () => {
    try {
      const [dados, err] = await handleApi(api.post("Materiais/SelCentrosCombo", {}));
      if (err) throw err;

      // queremos exatamente no formato que já estás a usar: [[ref, nome], ...]
      const lista = (Array.isArray(dados) ? dados : [])
        .map((x) => [String(x?.ref || "").trim(), String(x?.nome || "").trim()])
        .filter(([ref]) => !!ref)
        .sort((a, b) => String(a[1]).localeCompare(String(b[1]), "pt-PT"));

      setCentrosPossiveis(lista);
    } catch (e) {
      console.error(e);
      setCentrosPossiveis([]);
    }
  };

  const carregarCentrosUtilizador = async () => {
    try {
      const userId = userData?.id;
      if (!userId) {
        setCentrosUserRefs([]);
        return;
      }

      const [dados, err] = await handleApi(
        api.post("Materiais/SelCentrosUtilizador", { idutilizador: userId })
      );
      if (err) throw err;

      const refs = (dados || [])
        .map((x) => String(x?.ref || x?.refcentro || "").trim())
        .filter(Boolean);

      setCentrosUserRefs(refs);
    } catch (e) {
      console.error(e);
      setCentrosUserRefs([]);
    }
  };

  useEffect(() => {
    carregarCentrosPossiveis();
    carregarCentrosUtilizador();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  useEffect(() => {
    if (!Array.isArray(centrosPossiveis) || centrosPossiveis.length === 0) return;

    const todos = centrosPossiveis.map(([ref]) => String(ref || "").trim()).filter(Boolean);

    const userRefs = (Array.isArray(centrosUserRefs) ? centrosUserRefs : [])
      .map((x) => String(x || "").trim())
      .filter(Boolean);

    // se tiver centros associados, seleciona-os (desde que existam na lista)
    if (userRefs.length > 0) {
      const intersecao = todos.filter((r) => userRefs.includes(r));
      setCentrosSel(intersecao.length > 0 ? intersecao : [...todos]);
      return;
    }

    // se não tiver, seleciona todos
    setCentrosSel([...todos]);
  }, [centrosPossiveis, centrosUserRefs]);

  // ---------------------------------------------------------------------------
  // Carga da lista
  // ---------------------------------------------------------------------------
  const carregarMateriaisRisco = async () => {
    if (!podeVerRuturas) return;

    setLoading(true);

    try {
      const body = {
        diasVendas: 30, // por agora fixo
        mostrarInativos: mostrarInativos ? 1 : 0,
        refCentros: Array.from(
          new Set((centrosSel || []).map((x) => String(x || "").trim()).filter(Boolean))
        ),
      };

      const [dados, err] = await handleApi(api.post("Materiais/SelAnaliseStocksDashboard", body));

      if (err) {
        showAlert(err, "error");
        setMateriaisRisco([]);
        return;
      }

      // o handleApi já devolve diretamente o `data` do envelope
      if (!Array.isArray(dados)) {
        console.log("Resposta inesperada de SelAnaliseStocksDashboard:", dados);
        setMateriaisRisco([]);
        return;
      }

      // console.log("SelAnaliseStocksDashboard – materiais", dados.length);
      // console.log(Object.keys(dados?.[0] || {}));

      setMateriaisRisco(dados);

      carregarImagensMateriais(dados.map((m) => m.refmaterial));
    } catch (e) {
      console.error(e);
      showAlert("Erro ao carregar materiais com risco de rutura.", "error");
      setMateriaisRisco([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarMateriaisRisco();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVerRuturas, mostrarInativos, centrosUserRefs]);

  // ---------------------------------------------------------------------------
  // INATIVAR MATERIAL
  // ---------------------------------------------------------------------------
  const abrirDialogInativar = (mat) => {
    setMaterialRiscoSel(mat);
    setOpenDialogInativar(true);
  };

  const handleCancelarInativar = () => {
    setOpenDialogInativar(false);
    setMaterialRiscoSel(null);
  };

  const handleConfirmarInativar = async () => {
    if (!materialRiscoSel) return;

    try {
      const body = {
        refmaterial: materialRiscoSel.refmaterial,
        inativo: 1,
      };

      const res = await api.post("Materiais/UpdMaterialInativo", body);
      const dados = res.data;
      const msg = (dados && dados.message) || "Material inativado com sucesso.";

      showAlert(msg, "success");

      setMateriaisRisco((prev) => {
        const atualizada = prev.map((m) =>
          m.refmaterial === materialRiscoSel.refmaterial ? { ...m, inativo: 1 } : m
        );

        // esconder imediatamente se o switch estiver desligado
        return mostrarInativos ? atualizada : atualizada.filter((m) => m.inativo !== 1);
      });
    } catch {
      showAlert("Erro ao inativar material.", "error");
    } finally {
      handleCancelarInativar();
    }
  };

  // ---------------------------------------------------------------------------

  const handleOpenDetalhes = (row) => {
    if (!row) return;

    setMaterialDetalhe({
      refmaterial: row.refmaterial,
      ref: row.refmaterial,
      nome: row.nomematerial,
    });
    setDetalhesOpen(true);
  };

  const handleCloseDetalhes = () => {
    setDetalhesOpen(false);
    setMaterialDetalhe(null);
  };

  // ---------------------------------------------------------------------------
  // ALTERAR DIAS DE RUTURA
  // ---------------------------------------------------------------------------
  const handleChangeDiasRutura = (refmaterial, value) => {
    const limpo = value.replace(/[^\d]/g, "");
    setMateriaisRisco((prev) =>
      prev.map((m) => (m.refmaterial === refmaterial ? { ...m, diasrutura_temp: limpo } : m))
    );
  };

  const handleBlurDiasRutura = (row) => {
    // procurar o material atual na lista
    const matAtual = materiaisRisco.find((m) => m.refmaterial === row.refmaterial) || row;

    const atual = matAtual.diasrutura ?? 30;
    const tempStr =
      matAtual.diasrutura_temp !== undefined ? matAtual.diasrutura_temp : String(atual);

    if (tempStr === "") {
      // limpa → reverte
      setMateriaisRisco((prev) =>
        prev.map((m) =>
          m.refmaterial === matAtual.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      return;
    }

    const novo = parseInt(tempStr, 10);
    if (!Number.isFinite(novo) || novo < 0) {
      // inválido → reverte
      setMateriaisRisco((prev) =>
        prev.map((m) =>
          m.refmaterial === matAtual.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      showAlert("Valor de dias de rutura inválido.", "error");
      return;
    }

    // SE O VALOR NÃO MUDOU → NÃO ABRE DIALOG
    if (novo === atual) {
      setMateriaisRisco((prev) =>
        prev.map((m) =>
          m.refmaterial === matAtual.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      return;
    }

    // abre diálogo de confirmação
    setDialogDiasRutura({
      open: true,
      refmaterial: matAtual.refmaterial,
      nome: matAtual.nomematerial,
      valorAntigo: atual,
      valorNovo: novo,
    });
  };

  const handleCancelarDiasRutura = () => {
    // reverter campo temp
    setMateriaisRisco((prev) =>
      prev.map((m) =>
        m.refmaterial === dialogDiasRutura.refmaterial ? { ...m, diasrutura_temp: undefined } : m
      )
    );
    setDialogDiasRutura((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmarDiasRutura = async () => {
    const { refmaterial, valorNovo } = dialogDiasRutura;
    try {
      const body = {
        refmaterial,
        diasrutura: valorNovo,
      };

      const res = await api.post("Materiais/UpdMaterialDiasRutura", body);
      const dados = res.data;
      const msg = (dados && dados.message) || "Dias de rutura atualizados com sucesso.";

      showAlert(msg, "success");

      setMateriaisRisco((prev) =>
        prev.map((m) =>
          m.refmaterial === refmaterial
            ? { ...m, diasrutura: valorNovo, diasrutura_temp: undefined }
            : m
        )
      );
    } catch {
      showAlert("Erro ao atualizar dias de rutura.", "error");
    } finally {
      setDialogDiasRutura((prev) => ({ ...prev, open: false }));
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const cabecalhos = [
    "Fabricante",
    "", // miniatura
    "Refª Material",
    "Nome do material",
    "Qtd. Stock Total",
    "Enc.",
    "Pré-vendas",
    "Stock Dispon.",
    "Consumo diário (média ano)",
    "Dias Rutura (média ano)",
    "Consumo diário (prev. mês seg.)",
    "Dias Rutura (prev. mês seg.)",
  ];

  function norm(s) {
    return String(s || "")
      .toLowerCase()
      .trim();
  }

  const materiaisFiltrados = useMemo(() => {
    const fFab = norm(filtros.fabricante);
    const fRef = norm(filtros.refmaterial);
    const fNome = norm(filtros.nomematerial);

    return (Array.isArray(materiaisRisco) ? materiaisRisco : []).filter((m) => {
      if (fFab && !norm(m.fabricante).includes(fFab)) return false;
      if (fRef && !norm(m.refmaterial).includes(fRef)) return false;
      if (fNome && !norm(m.nomematerial).includes(fNome)) return false;
      return true;
    });
  }, [materiaisRisco, filtros]);

  const filtroInputStyle = {
    width: "100%",
    padding: "2px 4px",
    fontSize: 11,
    border: "none",
    borderBottom: "1px solid #e0e0e0",
    outline: "none",
    backgroundColor: "transparent",
  };

  const refsCentrosTodos = useMemo(
    () => (Array.isArray(centrosPossiveis) ? centrosPossiveis.map(([ref]) => ref) : []),
    [centrosPossiveis]
  );

  const toggleCentro = (refcentro) => {
    const ref = String(refcentro || "").trim();
    if (!ref) return;

    setCentrosSel((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      return prevArr.includes(ref) ? prevArr.filter((x) => x !== ref) : [...prevArr, ref];
    });
  };

  const renderCheckboxCentros = () => {
    if (!centrosPossiveis || centrosPossiveis.length === 0) return null;

    return (
      <MDBox px={2} pb={1}>
        <MDBox
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{
            flexWrap: "wrap",
            rowGap: 0.5,
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 2,
            p: 1,
            background: "#fff",
          }}
        >
          <MDTypography variant="button" fontWeight="regular" sx={{ mr: 0.5 }}>
            Centros:
          </MDTypography>

          {/* <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                size="small"
                checked={todosSelecionados}
                onChange={handleToggleAllCentros}
              />
            }
            label="Todos"
          /> */}

          {centrosPossiveis.map(([ref, nome]) => (
            <Tooltip key={ref} title={`${nome} (${ref})`} arrow>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={centrosSel.includes(ref)}
                    onChange={() => toggleCentro(ref)}
                  />
                }
                label={
                  <span style={{ display: "inline-flex", gap: 6, alignItems: "baseline" }}>
                    <span style={{ fontWeight: 600 }}>{nome}</span>
                    <span style={{ opacity: 0.55 }}>({ref})</span>
                  </span>
                }
              />
            </Tooltip>
          ))}

          <MDButton
            variant="gradient"
            color="info"
            onClick={carregarMateriaisRisco}
            disabled={!podeVerRuturas || loading}
          >
            Recalcular
          </MDButton>
        </MDBox>
      </MDBox>
    );
  };

  const sumByCentros = (arr, getRef, getQtd) => {
    const lista = Array.isArray(arr) ? arr : [];
    const ativos =
      Array.isArray(centrosSel) && centrosSel.length > 0 ? centrosSel : refsCentrosTodos;

    let total = 0;
    for (const it of lista) {
      const ref = String(getRef(it) || "").trim();
      if (!ref) continue;
      if (!ativos.includes(ref)) continue;

      const qtd = Number(getQtd(it));
      if (!Number.isNaN(qtd)) total += qtd;
    }
    return total;
  };

  const sumStocksCentros = (mat) => {
    if (Array.isArray(mat?.stocks_centros) && mat.stocks_centros.length > 0) {
      return sumByCentros(
        mat.stocks_centros,
        (c) => c.refcentro,
        (c) => c.quantidade
      );
    }
    return Number(mat?.qtdstocktotal) || 0;
  };

  const sumEncomendasCentros = (mat) => {
    // se mais tarde tiveres encomendas_centros, ativa automaticamente
    if (Array.isArray(mat?.encomendas_centros) && mat.encomendas_centros.length > 0) {
      return sumByCentros(
        mat.encomendas_centros,
        (c) => c.refcentro,
        (c) => c.quantidade_total ?? c.quantidade
      );
    }
    return Number(mat?.qtdencomendas) || 0;
  };

  const sumPreVendasCentros = (mat) => {
    // se mais tarde tiveres prevendas_centros, ativa automaticamente
    if (Array.isArray(mat?.prevendas_centros) && mat.prevendas_centros.length > 0) {
      return sumByCentros(
        mat.prevendas_centros,
        (c) => c.refcentro,
        (c) => c.quantidade_total ?? c.quantidade
      );
    }
    return Number(mat?.qtdprevendas) || 0;
  };

  // ---------------------------------------------------------------------------
  // SORTING (client-side)
  // ---------------------------------------------------------------------------
  const [sortConfig, setSortConfig] = useState({ key: null, desc: false });

  const handleSort = (key) => {
    // colunas sem sorting (miniatura, etc.)
    if (!key || key === "") return;

    setSortConfig((prev) => {
      if (prev.key === key) return { key, desc: !prev.desc }; // toggle ASC/DESC
      return { key, desc: false }; // novo sort → ASC
    });
  };

  const getSortValue = (mat, key) => {
    // Atenção: aqui usamos as mesmas contas do render (para ordenar “igual ao que se vê”)
    const qtdStockTotal = sumStocksCentros(mat);
    const qtdEnc = sumEncomendasCentros(mat);
    const qtdPrev = sumPreVendasCentros(mat);
    const qtdDispon = (Number(qtdStockTotal) || 0) + (Number(qtdEnc) || 0) - (Number(qtdPrev) || 0);

    const consumoMediaCentro = mat.consumo_diario_media_ano_centros ?? mat.consumo_diario_media_ano;
    const diasMediaCentro = mat.dias_rutura_media_ano_centros ?? mat.dias_rutura_media_ano;

    const consumoMesSegCentro = mat.consumo_diario_mes_seg_centros ?? mat.consumo_diario_mes_seg;
    const diasMax2MesesCentro = mat.dias_rutura_max_2meses_centros ?? mat.dias_rutura_max_2meses;

    switch (key) {
      case "Fabricante":
        return mat.fabricante ?? "";
      case "Refª Material":
        return mat.refmaterial ?? "";
      case "Nome do material":
        return mat.nomematerial ?? "";
      case "Qtd. Stock Total":
        return Number(qtdStockTotal) || 0;
      case "Enc.":
        return Number(qtdEnc) || 0;
      case "Pré-vendas":
        return Number(qtdPrev) || 0;
      case "Stock Dispon.":
        return Number(qtdDispon) || 0;
      case "Consumo diário (média ano)":
        return Number(consumoMediaCentro) || 0;
      case "Dias Rutura (média ano)":
        return Number(diasMediaCentro) || 0;
      case "Consumo diário (prev. mês seg.)":
        return Number(consumoMesSegCentro) || 0;
      case "Dias Rutura (prev. mês seg.)":
        return Number(diasMax2MesesCentro) || 0;
      default:
        return "";
    }
  };

  const materiaisOrdenados = useMemo(() => {
    const lista = Array.isArray(materiaisFiltrados) ? [...materiaisFiltrados] : [];
    if (!sortConfig?.key) return lista;

    const key = sortConfig.key;
    const desc = !!sortConfig.desc;

    const cmp = (a, b) => {
      const va = getSortValue(a, key);
      const vb = getSortValue(b, key);

      // null/undefined/"" vão para o fim
      const aEmpty = va === null || va === undefined || va === "";
      const bEmpty = vb === null || vb === undefined || vb === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      // números
      if (typeof va === "number" && typeof vb === "number") {
        return va === vb ? 0 : va < vb ? -1 : 1;
      }

      // strings (case-insensitive)
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sa.localeCompare(sb, "pt");
    };

    lista.sort((a, b) => (desc ? -cmp(a, b) : cmp(a, b)));
    return lista;
  }, [materiaisFiltrados, sortConfig, centrosSel]);

  const todosCentrosSelecionados =
    (centrosPossiveis || []).length > 0 &&
    (centrosSel || []).length === (centrosPossiveis || []).length;

  if (!podeVerRuturas) return null;

  return (
    <>
      <MDBox mt={2} mb={2}>
        <Card>
          <MDBox p={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
            <MDBox>
              <MDTypography variant="h6" gutterBottom>
                Materiais com risco de rutura
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Lista de mercadorias (FERT) em que pelo menos uma das colunas{" "}
                <strong>"Dias Rutura"</strong> está abaixo do limiar definido no material.
                <br />
                Com vendas há menos de 30 dias, ou previsíveis no mês seguinte.
              </MDTypography>
            </MDBox>

            <FormControlLabel
              control={
                <Switch
                  checked={mostrarInativos}
                  onChange={(e) => setMostrarInativos(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <MDTypography variant="button" color="text">
                  Mostrar materiais inativos
                </MDTypography>
              }
              sx={{ ml: 2 }}
            />
          </MDBox>

          {renderCheckboxCentros()}

          <MDBox px={2} pb={2}>
            {loading ? (
              <MDBox py={2} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </MDBox>
            ) : (
              <MDBox
                sx={{
                  height: "45vh",
                  overflow: "auto",
                  borderRadius: 1,
                  "& tbody tr:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr>
                      {/* Coluna dos botões / dias rutura */}
                      <th
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          background: "#fff",
                          textAlign: "left",
                          padding: "6px 8px",
                          fontSize: 12,
                          color: "#7b809a",
                          verticalAlign: "bottom",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                        }}
                      />
                      {cabecalhos.map((titulo) => (
                        <th
                          key={titulo}
                          onClick={() => handleSort(titulo)}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            cursor: titulo ? "pointer" : "default",
                            userSelect: "none",
                            whiteSpace: "nowrap",
                          }}
                          title={titulo ? "Ordenar" : undefined}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {/* mantém aqui o teu conteúdo atual (com tooltips) */}
                            {titulo === "Dias Rutura (média ano)" ? (
                              <span style={{ display: "inline-flex", alignItems: "center" }}>
                                Dias Rutura (média ano)
                                <Tooltip title={tooltipMediaAno} arrow>
                                  <InfoIcon sx={{ fontSize: 16, cursor: "pointer", ml: 0.5 }} />
                                </Tooltip>
                              </span>
                            ) : titulo === "Dias Rutura (máx. 2 meses)" ? (
                              <span style={{ display: "inline-flex", alignItems: "center" }}>
                                Dias Rutura (máx. 2 meses)
                                <Tooltip title={tooltipMax2Meses} arrow>
                                  <InfoIcon sx={{ fontSize: 16, cursor: "pointer", ml: 0.5 }} />
                                </Tooltip>
                              </span>
                            ) : (
                              titulo
                            )}

                            {/* indicador sort */}
                            {titulo && sortConfig.key === titulo && (
                              <span style={{ fontSize: 11, opacity: 0.75 }}>
                                {sortConfig.desc ? "▼" : "▲"}
                              </span>
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>

                    <tr>
                      {/* coluna botões sem filtro */}
                      <th
                        style={{
                          position: "sticky",
                          top: 40, // importante: fica por baixo do header
                          zIndex: 2,
                          background: "#fff",
                          padding: "4px 8px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                        }}
                      />

                      {/* Fabricante */}
                      <th
                        style={{
                          position: "sticky",
                          top: 40,
                          zIndex: 2,
                          background: "#fff",
                          padding: "4px 8px",
                        }}
                      >
                        <input
                          value={filtros.fabricante}
                          onChange={(e) =>
                            setFiltros((p) => ({ ...p, fabricante: e.target.value }))
                          }
                          style={filtroInputStyle}
                          placeholder="filtrar..."
                        />
                      </th>

                      {/* IMG (sem filtro) */}
                      <th
                        style={{
                          position: "sticky",
                          top: 40,
                          zIndex: 2,
                          background: "#fff",
                          padding: "4px 8px",
                        }}
                      />

                      {/* Refª Material */}
                      <th
                        style={{
                          position: "sticky",
                          top: 40,
                          zIndex: 2,
                          background: "#fff",
                          padding: "4px 8px",
                        }}
                      >
                        <input
                          value={filtros.refmaterial}
                          onChange={(e) =>
                            setFiltros((p) => ({ ...p, refmaterial: e.target.value }))
                          }
                          style={filtroInputStyle}
                          placeholder="filtrar..."
                        />
                      </th>

                      {/* Nome do material */}
                      <th
                        style={{
                          position: "sticky",
                          top: 40,
                          zIndex: 2,
                          background: "#fff",
                          padding: "4px 8px",
                        }}
                      >
                        <input
                          value={filtros.nomematerial}
                          onChange={(e) =>
                            setFiltros((p) => ({ ...p, nomematerial: e.target.value }))
                          }
                          style={filtroInputStyle}
                          placeholder="filtrar..."
                        />
                      </th>

                      {/* restantes colunas sem filtro (por agora) */}
                      {Array.from({ length: 8 }).map((_, i) => (
                        <th
                          key={`nf-${i}`}
                          style={{
                            position: "sticky",
                            top: 40,
                            zIndex: 2,
                            background: "#fff",
                            padding: "4px 8px",
                          }}
                        />
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {materiaisOrdenados.map((mat) => {
                      const limiar = mat.diasrutura ?? 30;

                      // const consumoMedia = mat.consumo_diario_media_ano;
                      // const diasMedia = mat.dias_rutura_media_ano;

                      // const consumoMesSeg = mat.consumo_diario_mes_seg;
                      // const diasMax2Meses = mat.dias_rutura_max_2meses;

                      const unidade = mat.unidade || "UN";

                      const qtdStockTotal = sumStocksCentros(mat);
                      const qtdEnc = sumEncomendasCentros(mat);
                      const qtdPrev = sumPreVendasCentros(mat);

                      // fórmula certa (como combinámos)
                      const qtdDispon =
                        (Number(qtdStockTotal) || 0) +
                        (Number(qtdEnc) || 0) -
                        (Number(qtdPrev) || 0);

                      // consumo/dias por centros (se a API já enviar campos por centros, usa-os; senão mantém os atuais)
                      const consumoMediaCentro =
                        mat.consumo_diario_media_ano_centros ?? mat.consumo_diario_media_ano;
                      const diasMediaCentro =
                        mat.dias_rutura_media_ano_centros ?? mat.dias_rutura_media_ano;

                      const consumoMesSegCentro =
                        mat.consumo_diario_mes_seg_centros ?? mat.consumo_diario_mes_seg;
                      const diasMax2MesesCentro =
                        mat.dias_rutura_max_2meses_centros ?? mat.dias_rutura_max_2meses;

                      return (
                        <tr key={mat.refmaterial}>
                          {/* BOTÕES / DIAS RUTURA EDITÁVEL */}
                          <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleOpenDetalhes(mat)}
                                title="Info"
                                sx={{
                                  border: "1px solid",
                                  borderColor: "info.main",
                                  borderRadius: 1,
                                }}
                              >
                                <InfoIcon fontSize="inherit" />
                              </IconButton>

                              {mat.inativo === 1 ? (
                                <IconButton
                                  size="small"
                                  onClick={() => abrirDialogAtivar(mat)}
                                  title="Ativar material"
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "success.main",
                                    borderRadius: 1,
                                  }}
                                >
                                  <CheckCircleOutlineIcon fontSize="inherit" />
                                </IconButton>
                              ) : (
                                <Tooltip
                                  title={
                                    !todosCentrosSelecionados
                                      ? "Para inativar, selecione todos os centros."
                                      : ""
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => abrirDialogInativar(mat)}
                                      title="Inativar material"
                                      disabled={!todosCentrosSelecionados}
                                      sx={{
                                        border: "1px solid",
                                        borderRadius: 1,
                                        borderColor: todosCentrosSelecionados
                                          ? "error.main"
                                          : "action.disabled",
                                        color: todosCentrosSelecionados
                                          ? "error.main"
                                          : "action.disabled",
                                      }}
                                    >
                                      <BlockIcon fontSize="inherit" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                              <TextField
                                value={mat.diasrutura_temp ?? mat.diasrutura ?? 30}
                                onChange={(e) =>
                                  handleChangeDiasRutura(mat.refmaterial, e.target.value)
                                }
                                onBlur={() => handleBlurDiasRutura(mat)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault(); // impede submit ou comportamentos estranhos
                                    handleBlurDiasRutura(mat); // ativa o mesmo fluxo que o blur
                                  }
                                }}
                                size="small"
                                sx={{ width: 60 }}
                                inputProps={{
                                  style: { textAlign: "center", fontSize: 12, padding: "4px 6px" },
                                }}
                              />
                            </MDBox>
                          </td>

                          {/* FABRICANTE */}
                          <td
                            style={{
                              padding: "6px 8px",
                              maxWidth: 160,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={mat.fabricante} // tooltip com o nome completo
                          >
                            {mat.fabricante || "-"}
                          </td>

                          {/* IMG */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {(() => {
                              const ref = String(mat.refmaterial || "").trim();
                              const url = imgsByRef?.[ref] || "";
                              if (!url)
                                return <span style={{ display: "inline-block", width: 32 }} />;

                              return (
                                <img
                                  src={url}
                                  alt={ref}
                                  title="Abrir imagem"
                                  style={{
                                    width: 26,
                                    height: 26,
                                    objectFit: "contain",
                                    borderRadius: 6,
                                    cursor: "pointer",
                                    border: "1px solid #eee",
                                    background: "#fff",
                                  }}
                                  onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                />
                              );
                            })()}
                          </td>

                          {/* REFª MATERIAL */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {mat.refmaterial}
                          </td>

                          {/* NOME */}
                          <td style={{ padding: "6px 8px" }}>{mat.nomematerial}</td>

                          {/* QTD STOCK TOTAL */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {qtdStockTotal != null
                              ? `${Number(qtdStockTotal).toLocaleString("pt-PT", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })} ${unidade}`
                              : "-"}
                          </td>

                          {/* ENCOMENDAS */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {qtdEnc != null
                              ? `${Number(qtdEnc).toLocaleString("pt-PT", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })} ${unidade}`
                              : "-"}
                          </td>

                          {/* PRÉ-VENDAS */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {qtdPrev != null
                              ? `${Number(qtdPrev).toLocaleString("pt-PT", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })} ${unidade}`
                              : "-"}
                          </td>

                          {/* STOCK DISPONÍVEL */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {qtdDispon != null
                              ? `${Number(qtdDispon).toLocaleString("pt-PT", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })} ${unidade}`
                              : "-"}
                          </td>

                          {/* CONSUMO DIÁRIO (MÉDIA ANO) */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {consumoMediaCentro == null
                              ? "-"
                              : `${Number(consumoMediaCentro).toLocaleString("pt-PT", {
                                  maximumFractionDigits: 3,
                                })} ${unidade}`}
                          </td>

                          {/* DIAS RUTURA (MÉDIA ANO) */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {diasMediaCentro == null ? (
                              "-"
                            ) : (
                              <span
                                style={{
                                  color: diasMediaCentro < limiar ? "#f44335" : "inherit",
                                  fontWeight: diasMediaCentro < limiar ? 600 : 400,
                                }}
                              >
                                {Number(diasMediaCentro).toLocaleString("pt-PT", {
                                  maximumFractionDigits: 1,
                                })}{" "}
                                dias
                              </span>
                            )}
                          </td>

                          {/* CONSUMO DIÁRIO (MÊS SEG.) */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {consumoMesSegCentro == null
                              ? "-"
                              : `${Number(consumoMesSegCentro).toLocaleString("pt-PT", {
                                  maximumFractionDigits: 3,
                                })} ${unidade}`}
                          </td>

                          {/* DIAS RUTURA (MÁX. 2 MESES) */}
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                            {diasMax2MesesCentro == null ? (
                              "-"
                            ) : (
                              <span
                                style={{
                                  color: diasMax2MesesCentro < limiar ? "#f44335" : "inherit",
                                  fontWeight: diasMax2MesesCentro < limiar ? 600 : 400,
                                }}
                              >
                                {Number(diasMax2MesesCentro).toLocaleString("pt-PT", {
                                  maximumFractionDigits: 1,
                                })}{" "}
                                dias
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {materiaisFiltrados.length === 0 && (
                      <tr>
                        <td
                          colSpan={cabecalhos.length + 1}
                          style={{
                            padding: "12px 8px",
                            textAlign: "center",
                            color: "#9e9e9e",
                            fontSize: 12,
                          }}
                        >
                          Sem dados com os filtros selecionados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </MDBox>
            )}
          </MDBox>

          {/* Dialog — Stocks/Vendas/Compras (MenuSearchMateriais) */}
          <MenuSearchMateriais
            open={detalhesOpen}
            onClose={handleCloseDetalhes}
            material={materialDetalhe}
            centrosSelecionados={centrosSel}
          />
        </Card>
      </MDBox>

      {/* Dialog — confirmar inativação */}
      <Dialog open={openDialogInativar} onClose={handleCancelarInativar} maxWidth="xs" fullWidth>
        <DialogTitle>Inativar material</DialogTitle>
        <DialogContent dividers>
          {materialRiscoSel && (
            <MDBox>
              <MDTypography variant="body2" mb={1}>
                Tem a certeza que pretende inativar o material:
              </MDTypography>
              <MDTypography fontWeight="bold">{materialRiscoSel.refmaterial}</MDTypography>
              <MDTypography fontWeight="bold" mb={2}>
                {materialRiscoSel.nomematerial}
              </MDTypography>
              <MDTypography variant="body2" color="error">
                Esta ação não pode ser anulada.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarInativar} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarInativar} color="error" variant="contained">
            Inativar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — confirmar alteração de dias de rutura */}
      <Dialog
        open={dialogDiasRutura.open}
        onClose={handleCancelarDiasRutura}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleConfirmarDiasRutura();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            handleCancelarDiasRutura();
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Alterar dias de rutura</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" mb={1}>
            Confirmar alteração dos dias de rutura do material:
          </MDTypography>
          <MDTypography fontWeight="bold">
            {dialogDiasRutura.refmaterial} — {dialogDiasRutura.nome}
          </MDTypography>
          <MDTypography variant="body2" mt={2}>
            De <strong>{dialogDiasRutura.valorAntigo}</strong> para{" "}
            <strong>{dialogDiasRutura.valorNovo}</strong> dias.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarDiasRutura} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarDiasRutura} color="info" variant="contained">
            Confirmar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — confirmar ativação */}
      <Dialog open={openDialogAtivar} onClose={handleCancelarAtivar} maxWidth="xs" fullWidth>
        <DialogTitle>Ativar material</DialogTitle>
        <DialogContent dividers>
          {materialRiscoSel && (
            <MDBox>
              <MDTypography variant="body2" mb={1}>
                Tem a certeza que pretende ativar o material:
              </MDTypography>
              <MDTypography fontWeight="bold">{materialRiscoSel.refmaterial}</MDTypography>
              <MDTypography fontWeight="bold" mb={2}>
                {materialRiscoSel.nomematerial}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarAtivar} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarAtivar} color="success" variant="contained">
            Ativar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
